// =============================================
// Kind of Document (acctg_docu_type) — per-account list + reading the kind off a document
// =============================================
// The standard kinds (Expense Receipt, Bank - Deposit, eTransfer Sent…) are the same for
// everyone and their CODE decides the rest: vendor or customer side (partySide.ts), which
// tax reports count a document (salesTaxReturn.ts). Migration 142 lets an account
//   * add its own kind that WORKS LIKE a standard one (base_docu_type_id) — it takes the
//     standard one's code, so everything above keeps working;
//   * rename or hide a standard kind for itself (an own row with overrides_docu_type_id).
//     Documents keep the standard kind's id; only the name shown changes.
// `effectiveDocuTypes` turns the raw table into the rows the screens use, with those
// rules applied, so code that looks a kind up by id or by code needs no change.
//
// `detectDocKind` reads the kind off the document's text (the free reader), and
// `hintToKindCode` maps what a reader (free or AI) called it to a standard code.
// Pure (no React, no I/O) so the rules can be unit-tested.

export type KindRowKind = 'standard' | 'edited-standard' | 'own';

export interface EffectiveDocuType {
  docu_type_id: number;          // the id a document is saved with
  docu_type_name: string;        // as this account shows it
  docu_type_code: string;        // the code the rules go by (an own kind: its base's)
  sort_order: number;
  is_active: number;
  is_deleted: number;            // 1 = not offered in the pickers (removed / hidden)
  row_kind: KindRowKind;
  standard_id: number | null;    // the standard kind it is, or works like
  own_row_id: number | null;     // this account's row (copy or own kind), if any
  [key: string]: any;
}

const isOff = (v: any) => Number(v) === 1;

/** The kinds one account sees: every standard kind (under this account's name for it,
 *  hidden ones marked is_deleted), then its own kinds after the kind each works like.
 *  Other accounts' rows are left out. */
export function effectiveDocuTypes(rows: any[], accountOwnerId: number | null): EffectiveDocuType[] {
  const live = rows.filter(r => !isOff(r.is_deleted));
  const standard = live.filter(r => r.account_owner_id == null);
  // eslint-disable-next-line eqeqeq
  const own = live.filter(r => accountOwnerId != null && r.account_owner_id != null && r.account_owner_id == accountOwnerId);
  const copyOf = new Map<number, any>();
  own.forEach(r => { if (r.overrides_docu_type_id != null) copyOf.set(Number(r.overrides_docu_type_id), r); });
  const byId = new Map(standard.map(r => [Number(r.docu_type_id), r] as const));

  const out: EffectiveDocuType[] = standard.map(r => {
    const copy = copyOf.get(Number(r.docu_type_id));
    return {
      ...r,
      docu_type_id: Number(r.docu_type_id),
      docu_type_name: String((copy?.docu_type_name ?? r.docu_type_name) || ''),
      docu_type_code: String(r.docu_type_code || ''),
      sort_order: Number(r.sort_order) || 0,
      is_active: 1,
      is_deleted: copy && Number(copy.is_active) === 0 ? 1 : 0,
      row_kind: copy ? 'edited-standard' : 'standard',
      standard_id: Number(r.docu_type_id),
      own_row_id: copy ? Number(copy.docu_type_id) : null,
    };
  });
  own.filter(r => r.overrides_docu_type_id == null).forEach(r => {
    const base = r.base_docu_type_id != null ? byId.get(Number(r.base_docu_type_id)) : undefined;
    out.push({
      ...r,
      docu_type_id: Number(r.docu_type_id),
      docu_type_name: String(r.docu_type_name || ''),
      docu_type_code: String(base?.docu_type_code || 'OTHR'),
      // right after the kind it works like
      sort_order: (Number(base?.sort_order) || 0) + 0.5,
      is_active: 1,
      is_deleted: Number(r.is_active) === 0 ? 1 : 0,
      row_kind: 'own',
      standard_id: base ? Number(base.docu_type_id) : null,
      own_row_id: Number(r.docu_type_id),
    });
  });
  return out.sort((a, b) => a.sort_order - b.sort_order || a.docu_type_name.localeCompare(b.docu_type_name));
}

/** A code for an account's own kind: unique, ≤ 20 characters ("U3-EXPS-7"). */
export function ownKindCode(accountOwnerId: number, baseCode: string, taken: Set<string>): string {
  const base = String(baseCode || 'OTHR').slice(0, 9);
  for (let i = 1; ; i++) {
    const code = `U${accountOwnerId}-${base}-${i}`.slice(0, 20);
    if (!taken.has(code)) return code;
  }
}

// ---- what a reader called it → a standard code ----

const HINT_CODES: Record<string, string> = {
  'receipt': 'EXPS', 'expense receipt': 'EXPS', 'sales receipt': 'EXPS', 'cash receipt': 'EXPS',
  'statement': 'STMT', 'bank statement': 'STMT', 'utility bill': 'STMT', 'bill': 'STMT',
  'utility bills / statements': 'STMT', 'credit card statement': 'STMT',
  'invoice': 'PINV', 'purchase invoice': 'PINV',
  'sales invoice': 'SINV',
  'report': 'RPT',
  'other': 'OTHR', 'other document': 'OTHR', 'contract': 'OTHR', 'agreement': 'OTHR', 'lease agreement': 'OTHR',
  'mortgage document': 'OTHR', 'tax document': 'OTHR', 'pay stub': 'OTHR', 'insurance document': 'OTHR',
  'statement of account': 'SOARcvd', 'statement of account (soa) received': 'SOARcvd',
  'deposit': 'DEP', 'bank deposit': 'DEP', 'deposit slip': 'DEP',
  'check': 'CHK', 'cheque': 'CHK', 'cheque copy': 'CHK', 'copy of cheque': 'CHK',
  'etransfer': 'ETRSent', 'e-transfer': 'ETRSent', 'etransfer sent': 'ETRSent', 'e-transfer sent': 'ETRSent',
  'etransfer received': 'ETRcvd', 'e-transfer received': 'ETRcvd',
  'payment': 'PMT', 'payment to vendor': 'PMT',
  'credit memo': 'CRMM', 'credit note': 'CRMM',
  'refund': 'RFNDRcvd', 'refund received': 'RFNDRcvd',
  'purchase order': 'QuoteRcvd', 'quotation': 'QuoteRcvd', 'quote': 'QuoteRcvd', 'estimate': 'QuoteRcvd',
};

/** The standard code a reader's label means ("Receipt" → EXPS, "eTransfer" → ETRSent);
 *  a label that already is a code is kept. '' when unknown. */
export function hintToKindCode(hint: string, knownCodes: string[] = []): string {
  const h = String(hint || '').trim();
  if (!h) return '';
  const known = knownCodes.find(c => c.toLowerCase() === h.toLowerCase());
  if (known) return known;
  return HINT_CODES[h.toLowerCase()] || '';
}

// ---- reading the kind off the text (free reader) ----

const has = (re: RegExp, t: string) => re.test(t);

/** The kind a document's text shows, as a standard code; '' when nothing is clear
 *  (the caller then leaves it to the user / "Other Document"). Order matters: a cheque
 *  or an e-transfer can also print "receipt" or "total". */
export function detectDocKind(text: string): string {
  const t = String(text || '').replace(/\s+/g, ' ');
  if (!t.trim()) return '';

  // e-Transfer notice / confirmation — direction from its wording
  if (has(/\binterac\b.{0,20}\be-?\s?transfer|\be-?\s?transfer\b/i, t)) {
    const rcvd = has(/\b(sent you|you('ve| have)? received|has been deposited|was deposited|deposited (in|into|to)|accepted|auto-?deposit)\b/i, t);
    const sent = has(/\b(you sent|has been sent|was sent|sent to|transfer sent|recipient|send money|you('ve| have) sent)\b/i, t);
    if (rcvd && !sent) return 'ETRcvd';
    return 'ETRSent';
  }
  // a cheque (or a copy of one)
  if (has(/\bpay\s+to\s+the\s+order\s+of\b|\bvoid\s+after\b|\/\s?100\s+dollars\b|\bcheque\s+(no\.?|number|#)|\bcheck\s+(no\.?|number|#)/i, t)) return 'CHK';
  // a bank deposit
  if (has(/\bdeposit\s+(slip|ticket|record|confirmation|details)\b|\bmobile\s+deposit\b|\bdeposited\s+(to|into)\s+(account|chequing|savings)\b/i, t)) return 'DEP';
  // a payment made from the bank: online bill / vendor payment confirmations, debit advices
  if (has(/\bpayments?\s+sent\b|\bsuccessfully\s+paid\b|\bbill\s+payments?\s+(made|confirmation|completed)\b|\bdebit\s+advice\b|\bpayment\s+advice\b|\bavis\s+de\s+paiement\b/i, t)
    || (has(/\btransaction\s+complete\b|\btransfer\s+complete\b/i, t) && has(/\bfrom\s*:?\s*(chequing|checking|savings|account)\b/i, t))) return 'PMT';
  // a credit memo — not the code legend printed on statements / advices ("C/M Credit Note")
  if (has(/\bcredit\s+(memo|note)\b/i, t.replace(/\bC\/M\s+credit\s+(memo|note)\b/gi, ''))) return 'CRMM';
  if (has(/\brefund(ed)?\b/i, t) && !has(/\bno\s+refunds?\b|\brefund\s+policy\b/i, t)) return 'RFNDRcvd';
  if (has(/\bstatement\s+of\s+account\b/i, t)) return 'SOARcvd';
  if (has(/\bquotation\b|\bquote\s*(no\.?|#|number)|\bestimate\s*(no\.?|#|number)|\bpurchase\s+order\b/i, t)) return 'QuoteRcvd';
  // a utility / phone / card bill
  if (has(/\b(billing|service)\s+period\b|\bstatement\s+(date|period)\b|\bpayment\s+due\s+date\b/i, t)
    || (has(/\b(hydro|electricity|natural gas|water|sewer|internet|wireless|mobility|telus|rogers|bell|sasktel|saskpower|saskenergy|shaw|enbridge|fortis|epcor|atco)\b/i, t)
      && has(/\b(bill|account\s+(no\.?|number)|amount\s+due|due\s+date)\b/i, t))) return 'STMT';

  // a till receipt: how many receipt marks it carries
  const marks = [
    /\bsub-?\s?total\b/i, /\b(gst|hst|pst|qst|vat|tax)\b/i, /\b(cash|change\s+due|change)\b/i,
    /\b(visa|mastercard|master card|amex|debit|interac|flash)\b/i, /\b(approved|auth(orization)?\s*(code|#|no))\b/i,
    /\bthank\s+you\b|\bthanks\s+for\b/i, /\b(server|table|cashier|till|register)\b/i, /\b(tip|gratuity)\b/i, /\btrans(action)?\s*(#|no|id)\b/i,
    /\bpaid\s+with\b/i, /\b\d+(\.\d+)?\s?(l|litres?|liters?)\b/i,
  ].filter(re => re.test(t)).length;
  const total = has(/\btotal(\s+due)?\b/i, t);
  const invoice = has(/\binvoice\b|\bbill\s+to\b|\bsold\s+to\b|\bterms\b|\bnet\s+\d+\b/i, t);
  if (has(/\breceipt\b/i, t) && (!invoice || marks >= 3)) return 'EXPS';
  if (invoice) return marks >= 4 && !has(/\bbill\s+to\b|\bsold\s+to\b/i, t) ? 'EXPS' : 'PINV';
  if (marks >= 3) return 'EXPS';
  // a short slip that prints a TOTAL (fuel, a corner store, a restaurant tab)
  if (total && (marks >= 2 || t.length < 400)) return 'EXPS';
  return '';
}

/** A plain label for a standard code (what the free reader writes as its hint, and in
 *  the purpose line); hintToKindCode maps each back to its code. */
export const KIND_LABEL: Record<string, string> = {
  EXPS: 'Receipt', STMT: 'Statement', PINV: 'Purchase Invoice', SINV: 'Sales Invoice', RPT: 'Report',
  OTHR: 'Other Document', SOARcvd: 'Statement of Account', DEP: 'Deposit', CHK: 'Cheque copy',
  ETRSent: 'eTransfer sent', ETRcvd: 'eTransfer received', PMT: 'Payment', CRMM: 'Credit Memo',
  RFNDRcvd: 'Refund', QuoteRcvd: 'Quotation',
};

/** Which kinds are money spent on something — the ones whose nature is "what was bought". */
export const EXPENSE_KIND_CODES = new Set(['EXPS', 'PINV', 'STMT', 'CHK', 'ETRSent', 'PMT']);
